// The deployment dry run (spec 4.4, A28) as the create steps and the Settings tab use it.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../api/context';
import type { DeploymentSettings, DeploymentValidation, Diagnostic } from '../api/types';
import { stableStringify } from '../lib/json';

export interface DryRunRequest {
  name: string;
  settings: DeploymentSettings;
}

export interface DryRunState {
  data: DeploymentValidation | null;
  error: unknown;
  pending: boolean;
}

/** `run` sends the dry run; a newer call aborts an older one, and only the last answer is kept. */
export function useDryRun(blueprintId: string, deploymentId?: string) {
  const api = useApi();
  const [state, setState] = useState<DryRunState>({ data: null, error: null, pending: false });
  const sequence = useRef(0);
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);

  const run = useCallback(
    async (request: DryRunRequest): Promise<DeploymentValidation | null> => {
      controller.current?.abort();
      const current = new AbortController();
      controller.current = current;
      const id = ++sequence.current;
      setState((s) => ({ ...s, pending: true }));
      try {
        const data = await api.validateDeployment(blueprintId, request, deploymentId, current.signal);
        if (id === sequence.current) setState({ data, error: null, pending: false });
        return data;
      } catch (error) {
        if (current.signal.aborted) return null;
        if (id === sequence.current) setState((s) => ({ ...s, error, pending: false }));
        return null;
      }
    },
    [api, blueprintId, deploymentId],
  );
  return { ...state, run };
}

/** Sends the dry run whenever the request changes, `delayMs` after the last change (the Settings tab, 6.13). */
export function useDebouncedDryRun(
  blueprintId: string,
  deploymentId: string,
  request: DryRunRequest | null,
  delayMs = 500,
): DryRunState {
  const { run, ...state } = useDryRun(blueprintId, deploymentId);
  const key = request ? stableStringify(request) : null;
  const latest = useRef(request);
  useEffect(() => {
    latest.current = request;
  });
  const first = useRef(true);
  useEffect(() => {
    if (key === null || !latest.current) return;
    const delay = first.current ? 0 : delayMs;
    first.current = false;
    const timer = setTimeout(() => void run(latest.current!), delay);
    return () => clearTimeout(timer);
  }, [key, delayMs, run]);
  return state;
}

/** The providers a deployment uses according to the dry run: its resources' and a managed vault's. */
export function dryRunProviders(validation: DeploymentValidation | null): string[] | null {
  if (!validation || validation.resources.length === 0) return null;
  const providers = new Set<string>();
  for (const resource of validation.resources) if (resource.provider) providers.add(resource.provider);
  if (validation.vault?.managed) providers.add(validation.vault.provider);
  return [...providers].sort();
}

export type CreateStep = 1 | 2 | 3 | 4;

/**
 * Where a diagnostic of the create (dry run or API) belongs (spec 6.4 Create): a step and the JSON Pointer of the
 * field it marks (`/settings/...` into the request body), or `null` for the step's general list.
 */
export function diagnosticTarget(d: Diagnostic): { step: CreateStep; field: string | null } {
  const path = d.path ?? '';
  if (path === '/name') return { step: 1, field: '/name' };
  if (path.startsWith('/settings/defaultPlacement')) return { step: 1, field: path };
  if (path.startsWith('/settings/cloudAccounts') || d.code === 'CLOUD_ACCOUNT_MISSING') {
    const provider = /'([a-z0-9-]+)'/.exec(d.message)?.[1];
    return {
      step: 2,
      field: path === '/settings/cloudAccounts' && provider ? `${path}/${provider}` : path || null,
    };
  }
  if (path.startsWith('/settings/parameters')) return { step: 3, field: path };
  if (path.startsWith('/parameters/')) return { step: 3, field: `/settings${path}` };
  if (d.code === 'MISSING_PARAMETER' || d.code.startsWith('PARAMETER_')) return { step: 3, field: null };
  if (
    path.startsWith('/settings/vault') ||
    path.startsWith('/settings/secretStore') ||
    path.startsWith('/settings/onFailure') ||
    path.startsWith('/settings/placementOverrides') ||
    d.code.startsWith('SECRET_VAULT')
  ) {
    return { step: 4, field: path.startsWith('/settings/') ? path : null };
  }
  return { step: 1, field: null };
}
